import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import DashboardLayout from "../../layouts/Dashboard";
import { Box, Grid, Paper, Stack, TextField, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import useLoader from "../../hooks/useLoader";
import useAlert from "../../hooks/useAlert";
import { planResultCRUD } from "../../http";
import { PLAN_RESULT_LIST_ROUTE } from "../../routes/const";
import { formatDateAsDate } from "../../functions/formatters";
import { useNCALayerClient } from "../../functions/sign/SignHooks";
import { js2xml } from "xml-js";

const safeStr = (v) => (v == null ? "" : String(v));

export default function PlanResultDetail() {
  const { id } = useParams();
  const { push } = useHistory();
  const alert = useAlert(false, 1);
  const { loading, start, stop } = useLoader(false);

  const [row, setRow] = useState(null);
  const [comment, setComment] = useState("");

  const [ncaClient] = useNCALayerClient();
  const ecpPendingRef = useRef(null);

  const fetchRow = useCallback(async () => {
    if (!id) return;
    const data = await planResultCRUD.get(id);
    setRow(data || null);
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        start();
        await fetchRow();
      } catch (e) {
        alert.show(0, e);
      } finally {
        stop();
      }
    })();
  }, [alert, fetchRow, start, stop]);

  const isSigned = useMemo(() => Boolean(row?.isSigned || row?.IsSigned), [row]);

  useEffect(() => {
    if (!ncaClient) return undefined;
    const prev = ncaClient.onmessage;
    ncaClient.onmessage = (msg) => {
      const pending = ecpPendingRef.current;
      if (pending) {
        ecpPendingRef.current = null;
        const ok = msg?.code === "200" || msg?.code === 200;
        if (ok && msg?.responseObject != null) {
          pending.resolve(msg.responseObject);
        } else {
          pending.reject(
            new Error(
              msg?.message ||
                (ok ? "Пустой ответ ЭЦП" : "Ошибка подписи через NCALayer")
            )
          );
        }
        return;
      }
      if (typeof prev === "function") prev(msg);
    };
    return () => {
      ncaClient.onmessage = prev;
    };
  }, [ncaClient]);

  const buildPlanResultSignXml = useCallback(() => {
    const payload = {
      PlanResultWorkflowSign: {
        _attributes: {
          planResultId: safeStr(id),
          atUtc: new Date().toISOString(),
        },
      },
    };
    return js2xml(payload, { compact: true });
  }, [id]);

  const signXmlWithNca = useCallback(
    (xml) =>
      new Promise((resolve, reject) => {
        if (!ncaClient?.signXml) {
          reject(
            new Error("NCALayer не готов. Установите NCALayer и обновите страницу.")
          );
          return;
        }
        if (ecpPendingRef.current) {
          reject(new Error("Подпись уже выполняется, подождите."));
          return;
        }
        ecpPendingRef.current = { resolve, reject };
        try {
          ncaClient.signXml("PKCS12", "SIGNATURE", xml);
        } catch (e) {
          ecpPendingRef.current = null;
          reject(e);
        }
      }),
    [ncaClient]
  );

  const onSign = useCallback(async () => {
    if (!id) return;
    try {
      start();
      const xml = buildPlanResultSignXml();
      const signedXml = await signXmlWithNca(xml);
      await planResultCRUD.workflowSignRunProtocol({
        id: Number(id),
        comment: safeStr(comment),
        sign: { xml, signedXml },
      });
      await fetchRow();
      alert.show(1, "Подписано");
    } catch (e) {
      alert.show(0, e);
    } finally {
      stop();
    }
  }, [alert, buildPlanResultSignXml, comment, fetchRow, id, signXmlWithNca, start, stop]);

  return (
    <DashboardLayout>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">PlanResult #{safeStr(id)}</Typography>
          <Box sx={{ flex: 1 }} />
          <LoadingButton variant="outlined" onClick={() => push(PLAN_RESULT_LIST_ROUTE)}>
            Назад
          </LoadingButton>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Создан"
              value={row?.createdAt ? formatDateAsDate(row.createdAt) : "—"}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Период с"
              value={row?.fromDate ? formatDateAsDate(row.fromDate) : "—"}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Период по"
              value={row?.endDate ? formatDateAsDate(row.endDate) : "—"}
              disabled
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Месяц"
              value={row?.month ? formatDateAsDate(row.month) : "—"}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Квартал"
              value={row?.quartal ? formatDateAsDate(row.quartal) : "—"}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Полугодие"
              value={row?.halfYear ? formatDateAsDate(row.halfYear) : "—"}
              disabled
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Филиал"
              value={row?.filial?.name || "—"}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Подразделение"
              value={row?.filialDivision?.name || "—"}
              disabled
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField fullWidth label="State" value={safeStr(row?.state)} disabled />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Подписан"
              value={isSigned ? "Да" : "Нет"}
              disabled
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Комментарий"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={isSigned}
            />
          </Grid>

          <Grid item xs={12}>
            <LoadingButton
              loading={loading}
              variant="contained"
              onClick={onSign}
              disabled={isSigned}
            >
              Подписать
            </LoadingButton>
          </Grid>
        </Grid>
      </Paper>
    </DashboardLayout>
  );
}

